import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import type { Lesson as LessonType, LessonStep } from '@notion-training/shared';
import { api } from '../lib/api.js';

type StepResponse = { correct: boolean; explanation?: string };

export function Lesson() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [stepIndex, setStepIndex] = useState(0);
  const [stepFeedback, setStepFeedback] = useState<StepResponse | null>(null);
  const [exploreClicked, setExploreClicked] = useState<Set<string>>(new Set());

  const lessonQuery = useQuery({
    queryKey: ['lesson', slug],
    queryFn: () => api.get<LessonType>(`/api/lessons/${slug}`),
    enabled: !!slug,
  });

  // The mutationFns take the slug as a runtime argument instead of closing
  // over it. React Router can keep this Lesson component mounted across slug
  // changes (e.g., via deep-links into the next lesson), and a closed-over
  // slug becomes stale — fires the OLD slug's completion call. Passing slug
  // through mutateAsync ensures every call uses the current path param.
  const stepMutation = useMutation({
    mutationFn: (input: { slug: string; stepIndex: number; answer?: number | string }) =>
      api.post<StepResponse>(`/api/progress/${input.slug}/step`, {
        stepIndex: input.stepIndex,
        answer: input.answer,
      }),
  });

  const completeMutation = useMutation({
    mutationFn: (slugArg: string) =>
      api.post<{ nextLessonSlug: string | null }>(`/api/progress/${slugArg}/complete`),
  });

  // React Router keeps the Lesson component mounted across slug param changes
  // (e.g., when "Next lesson →" navigates from /lesson/A to /lesson/B). Without
  // this reset the next lesson would inherit the previous lesson's stepIndex
  // and immediately render the "Lesson complete" screen.
  useEffect(() => {
    setStepIndex(0);
    setStepFeedback(null);
    setExploreClicked(new Set());
  }, [slug]);

  if (lessonQuery.isLoading) return <div style={{ padding: 32 }}>Loading…</div>;
  if (lessonQuery.error || !lessonQuery.data) {
    return (
      <div style={{ padding: 32 }}>
        <p style={{ color: 'var(--color-danger)' }}>This lesson isn't published yet.</p>
        <button className="btn" onClick={() => navigate('/')}>
          ← Home
        </button>
      </div>
    );
  }

  const lesson = lessonQuery.data;
  const totalSteps = lesson.steps.length + 1; // +1 for the Complete screen
  const isComplete = stepIndex >= lesson.steps.length;
  const step = isComplete ? null : lesson.steps[stepIndex];

  const advance = () => {
    setStepFeedback(null);
    setExploreClicked(new Set());
    setStepIndex((i) => i + 1);
  };

  const handleSubmit = async (answer: number | string) => {
    if (!slug) return;
    const res = await stepMutation.mutateAsync({ slug, stepIndex, answer });
    setStepFeedback(res);
  };

  return (
    <div className="view">
      <div style={{ height: 3, background: 'rgba(255,255,255,0.04)' }}>
        <div
          style={{
            height: '100%',
            background: 'var(--color-accent)',
            width: `${((stepIndex + 1) / totalSteps) * 100}%`,
            boxShadow: '0 0 12px rgba(79,233,181,0.5)',
            transition: 'width .4s cubic-bezier(.4,0,.2,1)',
          }}
        />
      </div>

      <div style={{ borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg-deep)' }}>
        <div
          style={{
            maxWidth: 1240,
            margin: '0 auto',
            padding: '14px 32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 18,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 12, color: 'var(--color-text-2)' }}>
            <button className="btn btn-tiny" onClick={() => navigate('/')}>
              ⌂ Home
            </button>
            <span style={{ color: 'var(--color-text-3)' }}>▌</span>
            <span style={{ color: 'var(--color-text-3)' }}>Level {lesson.level}</span>
            <span style={{ color: 'var(--color-text-3)' }}>/</span>
            <span>{lesson.title}</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text-3)' }}>
            Step {Math.min(stepIndex + 1, totalSteps)} / {totalSteps}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 780, margin: '0 auto', padding: '48px 32px 96px' }}>
        {!isComplete && step && (
          <StepRenderer
            step={step}
            feedback={stepFeedback}
            exploreClicked={exploreClicked}
            onClickExplore={(target) => setExploreClicked(new Set([...exploreClicked, target]))}
            onSubmit={handleSubmit}
            onContinue={advance}
            busy={stepMutation.isPending}
          />
        )}
        {isComplete && (
          <CompleteScreen
            isPending={completeMutation.isPending}
            onNext={async () => {
              if (!slug) return;
              const res = await completeMutation.mutateAsync(slug);
              qc.invalidateQueries({ queryKey: ['me'], refetchType: 'none' });
              qc.invalidateQueries({ queryKey: ['plan'], refetchType: 'none' });
              // Backend returns the next published lesson in the same level by
              // Order. If the user just finished the level's last lesson it's
              // null — fall back to home so they see "Level X done!".
              if (res.nextLessonSlug) {
                navigate(`/lesson/${res.nextLessonSlug}`);
              } else {
                navigate('/');
              }
            }}
            onHome={async () => {
              if (!slug) return;
              await completeMutation.mutateAsync(slug);
              qc.invalidateQueries({ queryKey: ['me'], refetchType: 'none' });
              qc.invalidateQueries({ queryKey: ['plan'], refetchType: 'none' });
              navigate('/');
            }}
          />
        )}
      </div>
    </div>
  );
}

function StepRenderer(props: {
  step: LessonStep;
  feedback: StepResponse | null;
  exploreClicked: Set<string>;
  onClickExplore: (target: string) => void;
  onSubmit: (answer: number | string) => Promise<void>;
  onContinue: () => void;
  busy: boolean;
}) {
  const { step } = props;
  switch (step.type) {
    case 'read':
      return <ReadStep step={step} onContinue={props.onContinue} />;
    case 'explore':
      return (
        <ExploreStep
          step={step}
          clicked={props.exploreClicked}
          onClick={props.onClickExplore}
          onContinue={props.onContinue}
        />
      );
    case 'quiz':
      return (
        <QuizStep
          step={step}
          feedback={props.feedback}
          onSubmit={props.onSubmit}
          onContinue={props.onContinue}
          busy={props.busy}
        />
      );
    case 'fill-in':
      return (
        <FillInStep
          step={step}
          feedback={props.feedback}
          onSubmit={props.onSubmit}
          onContinue={props.onContinue}
          busy={props.busy}
        />
      );
    case 'sandbox':
      return <SandboxStep step={step} onContinue={props.onContinue} />;
  }
}

function ReadStep({ step, onContinue }: { step: Extract<LessonStep, { type: 'read' }>; onContinue: () => void }) {
  return (
    <>
      <StepBadge label="Read" tag="read" />
      <h2 style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.025em', margin: '0 0 18px', lineHeight: 1.2 }}>
        {step.body}
      </h2>
      {step.analogy && (
        <div
          style={{
            background: 'linear-gradient(180deg, rgba(107,182,255,0.06), rgba(107,182,255,0.02))',
            border: '1px solid rgba(107,182,255,0.18)',
            borderLeft: '2px solid var(--color-info)',
            borderRadius: 10,
            padding: '14px 16px',
            marginBottom: 22,
          }}
        >
          <div
            style={{
              color: 'var(--color-info)',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            ▌ Think of it this way
          </div>
          <div style={{ fontSize: 13.5, lineHeight: 1.65 }}>{step.analogy}</div>
        </div>
      )}
      <div style={{ marginTop: 28 }}>
        <div
          style={{
            fontSize: 11,
            color: 'var(--color-text-3)',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            marginBottom: 12,
          }}
        >
          Key points
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {step.keyPoints.map((kp, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '24px 1fr', gap: 12, alignItems: 'start' }}>
              <span style={{ color: 'var(--color-accent)', fontWeight: 700, fontSize: 13 }}>{i + 1}</span>
              <span style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--color-text-2)' }}>{kp}</span>
            </div>
          ))}
        </div>
      </div>
      <button
        className="btn btn-primary"
        style={{ marginTop: 36, width: '100%', fontSize: 14, padding: 14 }}
        onClick={onContinue}
      >
        Continue ↓
      </button>
    </>
  );
}

function ExploreStep({
  step,
  clicked,
  onClick,
  onContinue,
}: {
  step: Extract<LessonStep, { type: 'explore' }>;
  clicked: Set<string>;
  onClick: (target: string) => void;
  onContinue: () => void;
}) {
  const allClicked = step.targets.every((t) => clicked.has(t));
  return (
    <>
      <StepBadge label="Explore" tag="explore" />
      <h2 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.025em', margin: '0 0 22px' }}>
        {step.instruction}
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {step.targets.map((target) => {
          const wasClicked = clicked.has(target);
          return (
            <button
              key={target}
              className="btn"
              onClick={() => onClick(target)}
              style={{
                justifyContent: 'space-between',
                width: '100%',
                ...(wasClicked
                  ? { borderColor: 'var(--color-accent-strong)', color: 'var(--color-accent)' }
                  : {}),
              }}
            >
              <span>{target}</span>
              <span>{wasClicked ? '✓' : '→'}</span>
            </button>
          );
        })}
      </div>
      <button
        className="btn btn-primary"
        style={{ marginTop: 24, width: '100%', fontSize: 14, padding: 14 }}
        disabled={!allClicked}
        onClick={onContinue}
      >
        {allClicked ? 'Continue ↓' : `Click all ${step.targets.length}`}
      </button>
    </>
  );
}

function QuizStep({
  step,
  feedback,
  onSubmit,
  onContinue,
  busy,
}: {
  step: Extract<LessonStep, { type: 'quiz' }>;
  feedback: StepResponse | null;
  onSubmit: (answer: number) => Promise<void>;
  onContinue: () => void;
  busy: boolean;
}) {
  const [picked, setPicked] = useState<number | null>(null);
  return (
    <>
      <StepBadge label="Quiz" tag="quiz" />
      <h2 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.025em', margin: '0 0 22px' }}>
        {step.question}
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {step.options.map((opt, i) => {
          const isPicked = picked === i;
          const isFeedback = feedback != null && isPicked;
          const correctState = isFeedback ? (feedback.correct ? 'correct' : 'incorrect') : null;
          return (
            <button
              key={i}
              className="btn"
              onClick={() => {
                if (feedback?.correct) return;
                setPicked(i);
              }}
              style={{
                justifyContent: 'flex-start',
                width: '100%',
                padding: '14px 16px',
                ...(correctState === 'correct'
                  ? { borderColor: 'var(--color-accent)', background: 'rgba(79,233,181,0.12)' }
                  : correctState === 'incorrect'
                    ? { borderColor: 'var(--color-danger)', background: 'rgba(255,110,110,0.08)' }
                    : isPicked
                      ? { borderColor: 'var(--color-accent-strong)' }
                      : {}),
              }}
            >
              <span style={{ marginRight: 12, fontWeight: 600, color: 'var(--color-text-3)' }}>
                {['A', 'B', 'C', 'D'][i]}
              </span>
              {opt}
            </button>
          );
        })}
      </div>
      {feedback && (
        <div
          style={{
            marginTop: 16,
            fontSize: 13.5,
            fontWeight: 500,
            color: feedback.correct ? 'var(--color-accent)' : 'var(--color-danger)',
          }}
        >
          {feedback.correct ? '✓ Correct' : '✗ Try again'}
        </div>
      )}
      {feedback?.explanation && (
        <div
          style={{
            marginTop: 18,
            padding: '14px 16px',
            borderRadius: 10,
            background: feedback.correct ? 'var(--color-accent-soft)' : 'rgba(255,110,110,0.08)',
            border: `1px solid ${feedback.correct ? 'rgba(79,233,181,0.28)' : 'rgba(255,110,110,0.28)'}`,
            fontSize: 13.5,
            lineHeight: 1.65,
          }}
        >
          {feedback.explanation}
        </div>
      )}
      <button
        className="btn btn-primary"
        style={{ marginTop: 22, width: '100%', fontSize: 14, padding: 14 }}
        disabled={busy || (!feedback?.correct && picked == null)}
        onClick={() => {
          if (feedback?.correct) onContinue();
          else if (picked != null) void onSubmit(picked);
        }}
      >
        {busy ? 'Checking…' : feedback?.correct ? 'Continue ↓' : 'Check answer'}
      </button>
    </>
  );
}

function FillInStep({
  step,
  feedback,
  onSubmit,
  onContinue,
  busy,
}: {
  step: Extract<LessonStep, { type: 'fill-in' }>;
  feedback: StepResponse | null;
  onSubmit: (answer: string) => Promise<void>;
  onContinue: () => void;
  busy: boolean;
}) {
  const [text, setText] = useState('');
  return (
    <>
      <StepBadge label="Fill in" tag="fillin" />
      <h2 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.025em', margin: '0 0 22px' }}>
        {step.question}
      </h2>
      <input
        type="text"
        className="btn"
        style={{ width: '100%', padding: 14, fontSize: 14, justifyContent: 'flex-start', textAlign: 'left' }}
        placeholder={step.placeholder ?? 'Type your answer…'}
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={1000}
      />
      {feedback && (
        <div
          style={{
            marginTop: 16,
            fontSize: 13.5,
            color: feedback.correct ? 'var(--color-accent)' : 'var(--color-danger)',
          }}
        >
          {feedback.correct ? '✓ Correct' : '✗ Try again'} {feedback.explanation && `— ${feedback.explanation}`}
        </div>
      )}
      <button
        className="btn btn-primary"
        style={{ marginTop: 22, width: '100%', fontSize: 14, padding: 14 }}
        disabled={busy || (!feedback?.correct && !text)}
        onClick={() => {
          if (feedback?.correct) onContinue();
          else void onSubmit(text);
        }}
      >
        {busy ? 'Checking…' : feedback?.correct ? 'Continue ↓' : 'Check'}
      </button>
    </>
  );
}

function SandboxStep({
  step,
  onContinue,
}: {
  step: Extract<LessonStep, { type: 'sandbox' }>;
  onContinue: () => void;
}) {
  // Practice mode: lazily provision the user's sandbox page via
  // POST /api/sandbox/ensure. The endpoint is idempotent and the page id
  // is stable per user, so staleTime: Infinity keeps the call to a single
  // request per session.
  const sandboxQuery = useQuery({
    queryKey: ['sandbox-page'],
    queryFn: () => api.post<{ pageId: string; url: string }>('/api/sandbox/ensure'),
    enabled: step.useSandboxPage,
    staleTime: Infinity,
    retry: 1,
  });

  return (
    <>
      <StepBadge label="Sandbox" tag="sandbox" />
      <h2 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.025em', margin: '0 0 12px' }}>
        {step.instruction}
      </h2>

      {!step.useSandboxPage && (
        <p style={{ color: 'var(--color-text-3)', fontSize: 13.5, marginBottom: 22 }}>
          Path: {step.targetPath.join(' › ')}
        </p>
      )}

      {step.useSandboxPage && sandboxQuery.isLoading && (
        <p style={{ color: 'var(--color-text-3)', fontSize: 13, marginBottom: 22 }}>
          ⏳ Preparing your sandbox in Notion…
        </p>
      )}

      {step.useSandboxPage && sandboxQuery.isError && (
        <div
          style={{
            background: 'var(--color-warn-soft)',
            border: '1px solid rgba(255,180,84,0.32)',
            borderRadius: 10,
            padding: '12px 14px',
            marginBottom: 22,
            fontSize: 13,
            color: 'var(--color-text-2)',
          }}
        >
          ⚠️ Couldn't reach your sandbox page. You can still do the practice in any Notion page you have access to —
          click <strong>Mark complete</strong> when you're done.
          <button
            type="button"
            onClick={() => void sandboxQuery.refetch()}
            style={{
              marginLeft: 8,
              background: 'transparent',
              color: 'var(--color-warn)',
              border: 0,
              padding: 0,
              fontSize: 12,
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {step.useSandboxPage && sandboxQuery.data && (
        <a
          href={sandboxQuery.data.url}
          target="_blank"
          rel="noopener noreferrer"
          className="btn"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 18,
            padding: '12px 18px',
            fontSize: 13.5,
            borderColor: 'var(--color-accent-strong)',
            color: 'var(--color-accent)',
          }}
        >
          ▸ Open my sandbox in Notion ↗
        </a>
      )}

      <button
        className="btn btn-primary"
        style={{ marginTop: 22, width: '100%', fontSize: 14, padding: 14 }}
        onClick={onContinue}
      >
        ✓ Mark complete ↓
      </button>
    </>
  );
}

function StepBadge({ label, tag }: { label: string; tag: 'read' | 'explore' | 'quiz' | 'fillin' | 'sandbox' }) {
  const colors: Record<string, { bg: string; color: string; border: string }> = {
    read: { bg: 'rgba(107,182,255,0.10)', color: 'var(--color-info)', border: 'rgba(107,182,255,0.22)' },
    explore: { bg: 'rgba(255,180,84,0.10)', color: 'var(--color-warn)', border: 'rgba(255,180,84,0.22)' },
    quiz: { bg: 'rgba(255,110,110,0.08)', color: 'var(--color-danger)', border: 'rgba(255,110,110,0.22)' },
    fillin: { bg: 'rgba(180,140,255,0.10)', color: 'var(--color-purple)', border: 'rgba(180,140,255,0.22)' },
    sandbox: { bg: 'var(--color-accent-soft)', color: 'var(--color-accent)', border: 'rgba(79,233,181,0.28)' },
  };
  const c = colors[tag] ?? colors['read']!;
  return (
    <div style={{ marginBottom: 22 }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '5px 10px',
          borderRadius: 7,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          background: c.bg,
          color: c.color,
          border: `1px solid ${c.border}`,
        }}
      >
        {label}
      </span>
    </div>
  );
}

function CompleteScreen({
  onNext,
  onHome,
  isPending,
}: {
  onNext: () => Promise<void>;
  onHome: () => Promise<void>;
  isPending: boolean;
}) {
  return (
    <div style={{ textAlign: 'center', paddingTop: 24 }}>
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          background: 'radial-gradient(circle at 50% 40%, rgba(79,233,181,0.35), transparent 70%)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 32,
          boxShadow: '0 0 0 1px rgba(79,233,181,0.2), 0 0 60px rgba(79,233,181,0.25)',
        }}
      >
        🎉
      </div>
      <h1 style={{ fontSize: 34, fontWeight: 700, letterSpacing: '-0.03em', margin: '24px 0 12px' }}>
        Lesson complete.
      </h1>
      <p style={{ color: 'var(--color-text-2)', fontSize: 15, lineHeight: 1.7, margin: '0 auto 32px', maxWidth: 520 }}>
        Streak +1 today. Keep the momentum going.
      </p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
        <button
          className="btn btn-primary"
          style={{ padding: '14px 28px' }}
          disabled={isPending}
          onClick={() => void onNext()}
        >
          {isPending ? 'Saving…' : '▸ Next lesson →'}
        </button>
        <button
          className="btn"
          style={{ padding: '14px 28px' }}
          disabled={isPending}
          onClick={() => void onHome()}
        >
          ↩ Back to home
        </button>
      </div>
    </div>
  );
}
